/**
 * @license
 * @copyright Copyright Motili Inc., 2025 All Rights Reserved
 */

import { createContext, useMemo, useState } from 'react';

import { useAppSelector } from 'common/stores/redux/hooks';
import photosIcon from 'images/svg/Camera.svg';
import { useWorkOrderPhotoTypesQuery } from 'common/stores/redux/MetaStore/MetaStoreApi';
import {
    DOCUMENT_TYPE,
    WORK_ORDER_STATUS,
    QUOTE_STATUS,
} from 'common/stores/redux/MetaStore/MetaStoreApi.types';
import Alert from 'common/components/Alert';
import { DocumentRequirementsPhotosUploadModal } from 'common/components/document/DocumentRequirementsPhotosModal';
import FileUploadDropzone from 'common/components/file-upload/FileUploadDropzone';
import {
    FormContainer,
    FormElement,
    FormRow,
    FormSectionHeader,
} from 'common/components/form/FormLayout';

import DocumentRequirementsPhotosUploaded from 'common/components/document/DocumentRequirementsPhotos/DocumentRequirementsPhotosUploaded';
import DocumentRequirementsPhotosRequired from 'common/components/document/DocumentRequirementsPhotos/DocumentRequirementsPhotosRequired';
import { LoadingSpinner } from '@motili/ui';

export interface Requirement {
    requirementTypeId: string;
    id?: string;
    name?: string;
    description?: string;
    percentMargin?: number;
    [key: string]: unknown;
}

interface Photo {
    id?: string;
    approvalStatusId?: string;
    fileName?: string;
    url?: string;
    typeId?: string;
    description?: string;
    uploadedAt?: string;
    uploadedBy?: string;
    [key: string]: unknown;
}

interface PhotoApproval {
    id?: string;
    photoId: string;
    status: string;
    notes?: string;
    approvedBy?: string;
    approvedAt?: string;
}

interface CheckIn {
    checkedOutAt?: string | null;
    checkedInUserId: number;
    checkedInAt?: string;
}

interface Document {
    statusId: string;
    documentTypeId: string;
    checkInsV2: CheckIn[];
    id?: string;
    name?: string;
    [key: string]: unknown;
}

interface AssetType {
    id: string;
    name: string;
    description?: string;
    [key: string]: unknown;
}

interface DocumentRequirementsPhotosProps {
    isLoading: boolean;
    clientRequirements?: Requirement[];
    projectRequirements?: Requirement[];
    photos?: Photo[];
    disabled?: boolean;
    acls?: boolean;
    removeAcls?: boolean;
    handleUploadPhotosOnClick?: (
        file: File,
        typeId: string,
        description: string,
        requiresPullAndClean: boolean
    ) => Promise<void>;
    handleReUploadPhotoOnClick?: (photoId: string) => void;
    handleSavePhotoOnClick?: (photo: Photo) => void;
    handleRemovePhotoOnClick?: (photoId: string) => void;
    handleEditPhotoRequirements?: (requirement: Requirement) => void;
    handleEditUploadedPhotoRequirement?: (
        photo: Photo,
        requirement: Requirement
    ) => void;
    handleGetApprovalHistoryPhotoRequirement?: (photoId: string) => void;
    handleSaveApprovalPhotoRequirement?: (
        photoId: string,
        status: string,
        notes?: string
    ) => void;
    handleBulkApprovalPhotoRequirement?: (
        photoIds: string[],
        status: string,
        notes?: string
    ) => void;
    handleEditApprovalPhotoRequirement?: (
        photoId: string,
        approval: PhotoApproval
    ) => void;
    document?: Document;
    assetTypes?: AssetType[];
    enableNotApplicablePhoto?: boolean;
}

interface AcceptedFile extends File {
    preview?: string;
}

const DocumentRequirementsPhotos = ({
    handleUploadPhotosOnClick,
    handleReUploadPhotoOnClick,
    handleSavePhotoOnClick,
    handleRemovePhotoOnClick,
    handleEditPhotoRequirements,
    handleEditUploadedPhotoRequirement,
    handleGetApprovalHistoryPhotoRequirement,
    handleSaveApprovalPhotoRequirement,
    handleBulkApprovalPhotoRequirement,
    handleEditApprovalPhotoRequirement,
    document,
    clientRequirements = [],
    projectRequirements = [],
    photos = [],
    disabled = false,
    acls = false,
    removeAcls = false,
    assetTypes = [],
    enableNotApplicablePhoto = false,
    isLoading,
}: DocumentRequirementsPhotosProps) => {
    const { isMotiliAccount, isContractorAccount, token } = useAppSelector(
        state => state.authentication
    );

    const { isClientEnablePhotoVerification } = useAppSelector(
        state => state.adminWorkOrder
    );

    const accountId = token?.userId;

    const [acceptedFiles, setAcceptedFiles] = useState<AcceptedFile[]>([]);
    const [currentPhotoTypeId, setCurrentPhotoTypeId] = useState<string | null>(
        null
    );
    const [filter, setFilter] = useState<string>('ALL');

    const { data: workOrderPhotoTypes } = useWorkOrderPhotoTypesQuery();

    const photoTypeRequirements = useMemo(() => {
        const _clientRequirements = clientRequirements || [];
        const _projectRequirements = projectRequirements || [];
        return [..._clientRequirements, ..._projectRequirements]?.filter(
            requirement => requirement.requirementTypeId === 'PHOTO'
        );
    }, [clientRequirements, projectRequirements]);

    const photoTypeContextValue = useMemo(
        () => ({ currentPhotoTypeId, setCurrentPhotoTypeId }),
        [currentPhotoTypeId, setCurrentPhotoTypeId]
    );

    const handleUploadOnClick = async ({
        typeId,
        description,
        requiresPullAndClean,
    }: {
        typeId: string;
        description: string;
        requiresPullAndClean: boolean;
    }) => {
        const file = acceptedFiles[0];
        try {
            await handleUploadPhotosOnClick?.(
                file,
                typeId,
                description,
                requiresPullAndClean
            );
            cleanUpFilesSoWeDontCauseAMemoryLeakAndCrashSomeonesBrowser(
                acceptedFiles
            );
            setAcceptedFiles([]);
            setCurrentPhotoTypeId(null);
        } catch (error) {
            cleanUpFilesSoWeDontCauseAMemoryLeakAndCrashSomeonesBrowser(
                acceptedFiles
            );
            setAcceptedFiles([]);
        }
    };

    const handleCancelOnClick = () => {
        cleanUpFilesSoWeDontCauseAMemoryLeakAndCrashSomeonesBrowser(
            acceptedFiles
        );
        setAcceptedFiles([]);
        setCurrentPhotoTypeId(null);
    };

    const handleCloseOnClick = () => {
        cleanUpFilesSoWeDontCauseAMemoryLeakAndCrashSomeonesBrowser(
            acceptedFiles
        );
        setAcceptedFiles([]);
        setCurrentPhotoTypeId(null);
    };

    const handleOnDrop = (
        newAcceptedFiles: AcceptedFile[],
        rejectedFiles: File[]
    ) => {
        setAcceptedFiles(newAcceptedFiles);
        if (rejectedFiles.length > 0) {
            Alert.warning(
                'The file you attempted to upload was not a valid image',
                {
                    autoClose: false,
                }
            );
            cleanUpFilesSoWeDontCauseAMemoryLeakAndCrashSomeonesBrowser(
                rejectedFiles
            );
        }
    };

    const allowUpload = allowUploadPhoto({
        document,
        isMotiliAccount,
        isContractorAccount,
        accountId,
    });

    const isQuote = document?.documentTypeId === DOCUMENT_TYPE.QUOTE;

    return (
        <DocumentRequirementsPhotosUploadModalContext.Provider
            value={photoTypeContextValue}
        >
            {acceptedFiles.length > 0 && (
                <DocumentRequirementsPhotosUploadModal
                    handleCloseOnClick={handleCancelOnClick}
                    handleCancelOnClick={handleCloseOnClick}
                    handleUploadOnClick={({
                        typeId,
                        description,
                        requiresPullAndClean,
                    }: {
                        typeId?: string;
                        description: string;
                        requiresPullAndClean: boolean;
                    }) =>
                        handleUploadOnClick({
                            typeId: typeId || currentPhotoTypeId!,
                            description,
                            requiresPullAndClean,
                        })
                    }
                    photoTypes={workOrderPhotoTypes || []}
                    currentPhotoTypeId={currentPhotoTypeId}
                    photoTypeRequirements={photoTypeRequirements}
                    photos={photos}
                    fileName={acceptedFiles[0].name}
                    imgSrc={acceptedFiles[0].preview}
                    mimeType={acceptedFiles[0].type}
                />
            )}
            <FormContainer>
                <FormSectionHeader title='Photos' imgSrc={photosIcon} />
                <FormRow>
                    <FormElement>
                        <FormRow>
                            <FormElement>
                                {isLoading && <LoadingSpinner size='medium' />}
                                {!isLoading && !isQuote && (
                                    <DocumentRequirementsPhotosRequired
                                        documentStatusId={document?.statusId}
                                        documentTypeId={
                                            document?.documentTypeId
                                        }
                                        photos={photos}
                                        photoTypes={workOrderPhotoTypes || []}
                                        photoTypeRequirements={
                                            photoTypeRequirements
                                        }
                                        handleEditPhotoRequirements={
                                            handleEditPhotoRequirements
                                        }
                                    />
                                )}
                            </FormElement>
                        </FormRow>
                    </FormElement>
                    <FormElement flex3>
                        <FileUploadDropzone
                            multiple={false}
                            accept={{
                                'image/jpeg': [],
                                'image/png': [],
                                'application/pdf': [],
                            }}
                            disabled={disabled || !allowUpload}
                            acls={acls || allowUpload}
                            handleOnDrop={handleOnDrop}
                            enableNotApplicablePhoto={enableNotApplicablePhoto}
                            readOnly={!allowUpload}
                        />
                    </FormElement>
                </FormRow>
            </FormContainer>
            <FormContainer>
                <PhotoUploadedHeaderWithFilter
                    filter={filter}
                    onFilterChange={setFilter}
                    photos={photos}
                />
                <FormRow>
                    <FormElement>
                        {isLoading ? (
                            <LoadingSpinner size='large' />
                        ) : (
                            <DocumentRequirementsPhotosUploaded
                                photos={photos}
                                filter={filter}
                                photoTypes={workOrderPhotoTypes || []}
                                photoTypeRequirements={photoTypeRequirements}
                                disabled={disabled}
                                acls={acls}
                                removeAcls={removeAcls}
                                handleSavePhotoOnClick={handleSavePhotoOnClick}
                                handleUploadPhotoOnClick={
                                    handleUploadPhotosOnClick
                                }
                                handleReUploadPhotoOnClick={
                                    handleReUploadPhotoOnClick
                                }
                                handleRemovePhotoOnClick={
                                    handleRemovePhotoOnClick
                                }
                                handleEditUploadedPhotoRequirement={
                                    handleEditUploadedPhotoRequirement
                                }
                                handleEditPhotoRequirements={
                                    handleEditPhotoRequirements
                                }
                                handleGetApprovalHistoryPhotoRequirement={
                                    handleGetApprovalHistoryPhotoRequirement
                                }
                                handleSaveApprovalPhotoRequirement={
                                    handleSaveApprovalPhotoRequirement
                                }
                                handleBulkApprovalPhotoRequirement={
                                    handleBulkApprovalPhotoRequirement
                                }
                                handleEditApprovalPhotoRequirement={
                                    handleEditApprovalPhotoRequirement
                                }
                                document={document}
                                assetTypes={assetTypes}
                                isClientEnablePhotoVerification={
                                    isClientEnablePhotoVerification
                                }
                            />
                        )}
                    </FormElement>
                </FormRow>
            </FormContainer>
        </DocumentRequirementsPhotosUploadModalContext.Provider>
    );
};

const allowUploadPhoto = (params: {
    document?: Document;
    isMotiliAccount: boolean;
    isContractorAccount: boolean;
    accountId: number;
}) => {
    const { document, isMotiliAccount, isContractorAccount, accountId } =
        params;

    if (!document) return false;

    if (isMotiliAccount) {
        if (document.documentTypeId === DOCUMENT_TYPE.WORK_ORDER) {
            const disableStatus = [
                WORK_ORDER_STATUS.COMPLETED,
                WORK_ORDER_STATUS.CLOSED,
                WORK_ORDER_STATUS.CANCELLED,
                WORK_ORDER_STATUS.INITIALIZED,
                WORK_ORDER_STATUS.DRAFT_CANCELLED,
            ];
            return !disableStatus.includes(
                document.statusId as WORK_ORDER_STATUS
            );
        }

        if (document.documentTypeId === DOCUMENT_TYPE.QUOTE) {
            const disableStatus = [
                QUOTE_STATUS.COMPLETED,
                QUOTE_STATUS.CLOSED,
                QUOTE_STATUS.CANCELLED,
                QUOTE_STATUS.DRAFT_CANCELLED,
            ];
            return !disableStatus.includes(document.statusId as QUOTE_STATUS);
        }
    }
    if (isContractorAccount) {
        if (document.documentTypeId === DOCUMENT_TYPE.WORK_ORDER) {
            const isCheckIn = !!document.checkInsV2?.find(
                checkIn =>
                    !checkIn.checkedOutAt &&
                    checkIn.checkedInUserId === accountId
            );

            const allowStatus = [WORK_ORDER_STATUS.IN_PROCESS];
            return (
                allowStatus.includes(document.statusId as WORK_ORDER_STATUS) &&
                isCheckIn
            );
        }
        if (document.documentTypeId === DOCUMENT_TYPE.QUOTE) {
            const allowStatus = [QUOTE_STATUS.REVISING];
            return allowStatus.includes(document.statusId as QUOTE_STATUS);
        }
    }
    return false;
};

interface PhotoUploadedHeaderWithFilterProps {
    filter: string;
    onFilterChange: (filter: string) => void;
    photos: Photo[];
}

const PhotoUploadedHeaderWithFilter = ({
    filter,
    onFilterChange,
    photos,
}: PhotoUploadedHeaderWithFilterProps) => {
    const actionItems = useMemo(() => {
        const rejectedPhotos = photos?.filter(
            photo => photo.approvalStatusId === 'REJECT'
        );
        if (photos.length === 0 || rejectedPhotos.length === 0) {
            return filter !== 'ALL' && onFilterChange('ALL');
        }
        return [
            {
                name: `Show All (${photos?.length || 0})`,
                handleSelect: () => filter !== 'ALL' && onFilterChange('ALL'),
                style: {
                    color: '#57c1e9',
                    whiteSpace: 'nowrap',
                },
            },
            {
                name: `Show Rejected Only (${rejectedPhotos?.length || 0})`,
                handleSelect: () =>
                    filter !== 'REJECT' && onFilterChange('REJECT'),
                style: {
                    color: '#e63232',
                    whiteSpace: 'nowrap',
                },
            },
        ];
    }, [filter, onFilterChange, photos]);

    return <FormSectionHeader title='Uploaded' actionItems={actionItems} />;
};

const cleanUpFilesSoWeDontCauseAMemoryLeakAndCrashSomeonesBrowser = (
    files: (File | AcceptedFile)[]
) => {
    try {
        files.forEach(file => {
            if ((file as AcceptedFile).preview) {
                window.URL.revokeObjectURL((file as AcceptedFile).preview!);
            }
        });
    } catch (error) {
        console.error(error);
    }
};

interface DocumentRequirementsPhotosUploadModalContextType {
    currentPhotoTypeId: string | null;
    setCurrentPhotoTypeId: (value: string | null) => void;
}

export const DocumentRequirementsPhotosUploadModalContext =
    createContext<DocumentRequirementsPhotosUploadModalContextType>({
        currentPhotoTypeId: null,
        setCurrentPhotoTypeId: () => {},
    });

export default DocumentRequirementsPhotos;
